import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { BaseDto } from '@models/base/baseDto.model';
import { DetalleTemplateBancoDto, GuardarTemplateBancoRequest, TemplateBancoDto } from '@models/templates-banco/templateBanco.model';
import { BancosService } from '@services/bancos/bancos.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TemplatesBancoService } from '@services/templates-banco/templates-banco.service';

/**
 * Parametrización de los archivos de transferencia por banco.
 *
 * Cada banco define su propio diseño de registro de ancho fijo. Al tenerlo como
 * datos, sumar un banco nuevo es cargar sus campos acá y no tocar código: el
 * generador (FileBankTransferService) arma el archivo recorriendo esta tabla.
 *
 * Lo crítico es el ANCHO: todas las líneas del archivo, incluido el pie, tienen
 * que medir lo mismo. Por eso la pantalla muestra el total en vivo.
 */
@Component({
  selector: 'app-parametrizacion-templates-banco',
  templateUrl: './parametrizacion-templates-banco.component.html',
  styleUrls: ['./parametrizacion-templates-banco.component.css']
})
export class ParametrizacionTemplatesBancoComponent implements OnInit {

  columnas: string[] = ['orden', 'nombreCampo', 'largo', 'tipo', 'alineacion', 'decimales', 'valorPorDefecto', 'accion'];

  templates: TemplateBancoDto[] = [];
  lstBancos: BaseDto[] = [];

  templateSeleccionado: TemplateBancoDto | null = null;
  form!: FormGroup;
  formBuilder: FormBuilder = new FormBuilder;

  dataSourceDetalle = new MatTableDataSource<DetalleTemplateBancoDto>();
  dataSourcePie = new MatTableDataSource<DetalleTemplateBancoDto>();

  /**
   * Nombres que el generador sabe resolver. Los dos últimos los calcula solo;
   * el resto salen de las propiedades de FileDataBankTransferDto. Un nombre que
   * no esté en esta lista se exporta vacío, así que conviene elegir de acá.
   */
  camposDisponibles: string[] = [
    'CBU_Cuenta_Origen',
    'ALIAS_CBU_Cuenta_Origen',
    'CBU_Cuenta_Destino',
    'ALIAS_CBU_Destino',
    'Importe',
    'Concepto',
    'Motivo',
    'Referencia',
    'EmailPropietario',
    'Relleno',
    'TotalImporte',
    'CuentaRegistro'
  ];

  constructor(
    private templatesBancoService: TemplatesBancoService,
    private bancosService: BancosService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService
  ) {
    this.form = this.formBuilder.group({
      Descripcion: ['', Validators.required],
      IdBanco: ['', Validators.required],
      NombreArchivo: ['', Validators.required],
      ExtensionArchivo: ['txt', Validators.required]
    });
  }

  ngOnInit(): void {
    this.getBancos();
    this.getTemplates();
  }

  //#region Carga

  getBancos() {
    this.bancosService.getAll().subscribe(
      data => this.lstBancos = data,
      error => this.snackBar.showError(error, "Error")
    );
  }

  getTemplates() {
    this.spinner.show("spTemplatesBanco");

    this.templatesBancoService.getAllConDetalles().subscribe(
      data => {
        this.spinner.hide("spTemplatesBanco");
        this.templates = isFalsy(data) ? [] : data;
      },
      error => {
        this.spinner.hide("spTemplatesBanco");
        this.snackBar.showError(error, "Error");
      }
    );
  }

  seleccionarTemplate(template: TemplateBancoDto) {
    this.templateSeleccionado = template;

    this.form.controls["Descripcion"].setValue(template.descripcion);
    this.form.controls["IdBanco"].setValue(isFalsy(template.banco) ? null : template.banco.id);
    this.form.controls["NombreArchivo"].setValue(template.nombreArchivo);
    this.form.controls["ExtensionArchivo"].setValue(template.extensionArchivo);

    const campos = isFalsy(template.detallesTemplateBanco) ? [] : [...template.detallesTemplateBanco];

    this.dataSourceDetalle.data = campos.filter(c => !c.esUltimaLinea).sort((a, b) => a.ordenCampo - b.ordenCampo);
    this.dataSourcePie.data = campos.filter(c => c.esUltimaLinea).sort((a, b) => a.ordenCampo - b.ordenCampo);
  }

  nuevoTemplate() {
    this.templateSeleccionado = new TemplateBancoDto();
    this.form.reset({ ExtensionArchivo: 'txt' });
    this.dataSourceDetalle.data = [];
    this.dataSourcePie.data = [];
  }

  //#endregion

  //#region Campos

  agregarCampo(esPie: boolean) {
    const lista = esPie ? this.dataSourcePie.data : this.dataSourceDetalle.data;

    const campo = new DetalleTemplateBancoDto();
    campo.esUltimaLinea = esPie;
    campo.concatenaEnUltimaLinea = esPie;
    campo.ordenCampo = lista.length === 0 ? 1 : Math.max(...lista.map(c => c.ordenCampo)) + 1;
    campo.alineacionIzq = true;
    campo.largoCampo = 1;

    const nueva = [...lista, campo];

    if (esPie)
      this.dataSourcePie.data = nueva;
    else
      this.dataSourceDetalle.data = nueva;
  }

  quitarCampo(campo: DetalleTemplateBancoDto, esPie: boolean) {
    const lista = esPie ? this.dataSourcePie.data : this.dataSourceDetalle.data;
    const nueva = lista.filter(c => c !== campo);

    if (esPie)
      this.dataSourcePie.data = nueva;
    else
      this.dataSourceDetalle.data = nueva;
  }

  mover(campo: DetalleTemplateBancoDto, esPie: boolean, direccion: number) {
    const lista = [...(esPie ? this.dataSourcePie.data : this.dataSourceDetalle.data)];
    const i = lista.indexOf(campo);
    const j = i + direccion;

    if (i < 0 || j < 0 || j >= lista.length)
      return;

    lista[i] = lista[j];
    lista[j] = campo;

    // El orden que vale es el de la lista: se renumera para que quede consistente
    lista.forEach((c, indice) => c.ordenCampo = indice + 1);

    if (esPie)
      this.dataSourcePie.data = lista;
    else
      this.dataSourceDetalle.data = lista;
  }

  /** Numérico y texto son excluyentes en el relleno: uno con ceros, otro con espacios */
  onCambiaTipo(campo: DetalleTemplateBancoDto) {
    if (!campo.esNumerico)
      campo.completaConCeros = false;
  }

  onCambiaAlineacion(campo: DetalleTemplateBancoDto, izquierda: boolean) {
    campo.alineacionIzq = izquierda;
    campo.alineacionDer = !izquierda;
  }

  //#endregion

  //#region Anchos

  getLargoDetalle(): number {
    return this.dataSourceDetalle.data.reduce((suma, c) => suma + (Number(c.largoCampo) || 0), 0);
  }

  getLargoPie(): number {
    return this.dataSourcePie.data.reduce((suma, c) => suma + (Number(c.largoCampo) || 0), 0);
  }

  /**
   * El archivo es de ancho fijo: si el pie no mide lo mismo que el detalle, el
   * banco rechaza el archivo completo. Se avisa acá y no cuando ya se importó.
   */
  anchosCoinciden(): boolean {
    return this.getLargoPie() === 0 || this.getLargoPie() === this.getLargoDetalle();
  }

  //#endregion

  guardar() {
    if (this.form.invalid) {
      this.snackBar.showError("Completá los datos del template.", "Atencion");
      return;
    }

    if (this.dataSourceDetalle.data.length === 0) {
      this.snackBar.showError("El template tiene que tener al menos un campo de detalle.", "Atencion");
      return;
    }

    if (!this.anchosCoinciden()) {
      this.snackBar.showError(
        `El pie mide ${this.getLargoPie()} y el detalle ${this.getLargoDetalle()}. Agregá un campo de relleno al pie.`,
        "Atencion");
      return;
    }

    const request = new GuardarTemplateBancoRequest();
    request.id = isFalsy(this.templateSeleccionado) ? 0 : this.templateSeleccionado!.id;
    request.descripcion = this.form.controls["Descripcion"].value;
    request.nombreArchivo = this.form.controls["NombreArchivo"].value;
    request.extensionArchivo = this.form.controls["ExtensionArchivo"].value;

    // Id_Banco e Id_TipoTemplateBanco son NOT NULL en la base. Un undefined
    // desaparece del JSON y llega vacío al back, así que se resuelven contra el
    // template seleccionado antes de mandar.
    request.idBanco = isFalsy(this.form.controls["IdBanco"].value)
      ? (isFalsy(this.templateSeleccionado?.banco) ? null : this.templateSeleccionado!.banco.id)
      : this.form.controls["IdBanco"].value;

    // idTipoTemplateBanco no viaja: hay un solo tipo y lo resuelve el back.

    if (isFalsy(request.idBanco)) {
      this.snackBar.showError("Elegí el banco del template.", "Atencion");
      return;
    }

    // El orden se renumera al guardar, para que coincida con lo que se ve
    const detalle = this.dataSourceDetalle.data.map((c, i) => ({ ...c, ordenCampo: i + 1, esUltimaLinea: false }));
    const pie = this.dataSourcePie.data.map((c, i) => ({ ...c, ordenCampo: detalle.length + i + 1, esUltimaLinea: true }));

    request.campos = [...detalle, ...pie];

    this.spinner.show("spTemplatesBanco");

    this.templatesBancoService.guardar(request).subscribe(
      () => {
        this.spinner.hide("spTemplatesBanco");
        this.snackBar.showSuccess("Template guardado correctamente.", "Exito");
        this.getTemplates();
      },
      error => {
        this.spinner.hide("spTemplatesBanco");
        this.snackBar.showError(error, "Error");
      }
    );
  }

  trackByItems(index: number, item: any): any { return index; }
}
